import { graphql } from 'react-apollo';

import { withProps } from 'recompose';

import { tupleToStringKey } from 'js/lib/stringKeyTuple';
import user from 'js/lib/user';
import waitForGraphQL from 'js/lib/waitForGraphQL';

import GetProductOwnership from 'bundles/entitlements-common/queries/GetProductOwnershipQuery.graphql';
import type {
  GetProductOwnershipQuery,
  GetProductOwnershipQueryVariables,
} from 'bundles/entitlements-common/queries/__generated__/GetProductOwnershipQuery';
import { formatGraphqlProductOnwership } from 'bundles/entitlements-common/utils/entitlementProductOwnershipUtils';
import type { FormattedGraphqlProductOwnership } from 'bundles/entitlements-common/utils/entitlementProductOwnershipUtils';
import { SPECIALIZATION, VERIFIED_CERTIFICATE } from 'bundles/payments-common/common/ProductType';

export type ProductOwnershipsInputProps = {
  productOwnershipsId?: string;
};
export type OptionProps = {
  isNotRequired?: boolean;
};

type InputProps = ProductOwnershipsInputProps;

export type PropsFromWithProductOwnerships = {
  productOwnerships?: FormattedGraphqlProductOwnership;
};

/**
 * Higher-order component that fetches product ownership data.
 * Provides product ownership information to wrapped components.
 *
 * @param options - Configuration options for the HOC
 * @param options.isNotRequired - If true, the component won't wait for the query to complete
 * @returns HOC that provides product ownership data
 * @deprecated Use the useProductOwnerships custom hook instead
 */
export const withProductOwnerships = <WithProductOwnershipsProps extends InputProps>({
  isNotRequired = false,
}: OptionProps = {}) => {
  const graphqlHoc = !isNotRequired ? waitForGraphQL : graphql;

  return graphqlHoc<
    WithProductOwnershipsProps,
    GetProductOwnershipQuery,
    GetProductOwnershipQueryVariables,
    PropsFromWithProductOwnerships
  >(GetProductOwnership, {
    skip: (props) => !user.isAuthenticatedUser() || !props.productOwnershipsId,
    options: ({ productOwnershipsId }) => {
      if (!productOwnershipsId) {
        throw new Error(`expected valid productOwnershipsId`);
      }
      return {
        variables: {
          productOwnershipId: productOwnershipsId,
        },
        context: { clientName: 'gatewayGql' },
      };
    },
    props: ({ data, ownProps }) => {
      return {
        ...ownProps,
        productOwnerships: formatGraphqlProductOnwership(data?.Entitlement?.queryProductOwnership),
      };
    },
  });
};

type SpecializationIdProps = {
  s12nId: string;
};

/**
 * HOC that generates a product ownership ID for a specialization.
 *
 * @param props - Component props containing specialization ID
 * @param props.s12nId - The specialization ID
 * @returns Object with generated product ownership ID
 */
export const withSpecializationProductOwnershipsId = withProps<ProductOwnershipsInputProps, SpecializationIdProps>(
  ({ s12nId }) => {
    const productOwnershipsId = user.isAuthenticatedUser()
      ? tupleToStringKey([user.get().id, SPECIALIZATION, s12nId])
      : undefined;
    return { productOwnershipsId };
  }
);

type VerifiedCertificateIdProps = {
  courseId?: string;
};

/**
 * HOC that generates a product ownership ID for a verified certificate course.
 *
 * @param props - Component props containing course ID
 * @param props.courseId - The course ID
 * @returns Object with generated product ownership ID
 */
export const withVerifiedCertificateProductOwnershipsId = withProps<
  ProductOwnershipsInputProps,
  VerifiedCertificateIdProps
>(({ courseId }) => {
  const productOwnershipsId =
    user.isAuthenticatedUser() && courseId
      ? tupleToStringKey([user.get().id, VERIFIED_CERTIFICATE, courseId])
      : undefined;
  return { productOwnershipsId };
});
